import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Check, Download, Star, CalendarCheck, FileSignature } from "lucide-react";
import api from "@/lib/api";
import DashboardShell from "@/components/layout/DashboardShell";
import { customerMenu } from "@/config/menus";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { StatusPill, inr, LoadingScreen } from "@/components/common";

const RATING_FIELDS = [["service_quality", "Service Quality"], ["guard_behaviour", "Guard Behaviour"], ["agency_response", "Agency Response"], ["attendance", "Attendance"], ["replacement_speed", "Replacement Speed"], ["professionalism", "Professionalism"]];

export default function ContractDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [att, setAtt] = useState([]);
  const [review, setReview] = useState({ service_quality: 5, guard_behaviour: 5, agency_response: 5, attendance: 5, replacement_speed: 5, professionalism: 5, comment: "" });
  const [reviewOpen, setReviewOpen] = useState(false);

  const load = async () => {
    const { data } = await api.get(`/contracts/${id}`); setC(data);
    api.get(`/attendance/contract/${id}`).then((r) => setAtt(r.data)).catch(() => {});
  };
  useEffect(() => { load().catch(() => {}); }, [id]);
  if (!c) return <DashboardShell menu={customerMenu} title="Contract"><LoadingScreen /></DashboardShell>;

  const accept = async () => {
    try { const { data } = await api.post(`/contracts/${id}/accept`); setC(data); toast.success("Contract accepted."); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitReview = async () => {
    try {
      await api.post("/reviews", { contract_id: id, agency_id: c.agency_id, ...review });
      toast.success("Review submitted. Thank you!"); setReviewOpen(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to submit review"); }
  };

  const terms = [["Service Location", c.service_location], ["Number of Guards", c.num_guards], ["Shift Timing", c.shift_timing],
    ["Monthly Charges", inr(c.monthly_charges)], ["Contract Duration", `${c.contract_duration_months} months`], ["Start Date", c.start_date],
    ["Replacement Terms", c.replacement_terms], ["Attendance Terms", c.attendance_terms], ["SLA", c.sla],
    ["Payment Terms", c.payment_terms], ["Termination", c.termination_terms]];

  return (
    <DashboardShell menu={customerMenu} title="Contract Details">
      <button onClick={() => navigate("/contracts")} className="flex items-center gap-1 text-sm text-navy-500 hover:text-brand mb-4"><ArrowLeft size={16} /> All contracts</button>

      <div className="bg-white border border-navy-200 rounded-xl overflow-hidden mb-6">
        <div className="bg-navy-900 text-white p-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="brand-label text-brand">Digital Contract</div>
            <h2 className="font-heading text-2xl font-bold mt-1">{c.agency_name}</h2>
            <p className="text-navy-300 text-sm mt-1">Contract ID: <span className="font-mono">{c.id}</span></p>
          </div>
          <StatusPill status={c.status} />
        </div>
        <div className="p-6 grid sm:grid-cols-2 gap-x-8 gap-y-4">
          {terms.map(([l, v]) => (
            <div key={l} className="flex justify-between border-b border-navy-100 pb-2">
              <span className="text-sm text-navy-500">{l}</span><span className="text-sm font-medium text-navy-900 text-right max-w-[60%]">{v}</span>
            </div>
          ))}
        </div>
        <div className="p-6 border-t border-navy-100 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-4 text-sm">
            <span className="flex items-center gap-1">{c.customer_accepted ? <Check size={16} className="text-emerald-500" /> : <FileSignature size={16} className="text-amber-500" />} Customer {c.customer_accepted ? "accepted" : "pending"}</span>
            <span className="flex items-center gap-1">{c.agency_accepted ? <Check size={16} className="text-emerald-500" /> : <FileSignature size={16} className="text-amber-500" />} Agency {c.agency_accepted ? "accepted" : "pending"}</span>
          </div>
          <div className="flex-1" />
          {!c.customer_accepted && c.status === "PENDING_ACCEPTANCE" && <Button className="bg-brand hover:bg-brand-600" onClick={accept} data-testid="accept-contract-btn"><Check size={16} className="mr-1" /> Digitally Accept</Button>}
          <Button variant="outline" className="border-navy-300" onClick={() => window.print()} data-testid="download-contract-btn"><Download size={16} className="mr-1" /> Download</Button>
          {c.status === "ACTIVE" && (
            <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
              <DialogTrigger asChild><Button variant="outline" className="border-navy-300" data-testid="review-btn"><Star size={16} className="mr-1" /> Rate Service</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle className="font-heading">Rate {c.agency_name}</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  {RATING_FIELDS.map(([k, l]) => (
                    <div key={k} className="flex items-center justify-between">
                      <Label className="text-sm">{l}</Label>
                      <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => (
                        <button key={n} onClick={() => setReview({ ...review, [k]: n })} data-testid={`rate-${k}-${n}`}>
                          <Star size={20} className={n <= review[k] ? "fill-amber-400 text-amber-400" : "text-navy-300"} />
                        </button>
                      ))}</div>
                    </div>
                  ))}
                  <Textarea placeholder="Share your experience…" value={review.comment} onChange={(e) => setReview({ ...review, comment: e.target.value })} data-testid="review-comment" />
                </div>
                <DialogFooter><Button className="bg-brand hover:bg-brand-600" onClick={submitReview} data-testid="submit-review-btn">Submit Review</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Attendance */}
      <div className="bg-white border border-navy-200 rounded-xl p-6">
        <div className="flex items-center gap-2 mb-4"><CalendarCheck size={18} className="text-brand" /><h3 className="font-heading font-semibold text-navy-900">Attendance Report</h3></div>
        {att.length === 0 ? <p className="text-navy-500 text-sm">No attendance records yet.</p> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[500px]">
              <thead className="bg-navy-50 text-navy-500 text-xs uppercase"><tr><th className="text-left p-2.5">Date</th><th className="text-left p-2.5">Guard</th><th className="p-2.5">Status</th><th className="p-2.5">Method</th><th className="p-2.5">In</th><th className="p-2.5">Out</th></tr></thead>
              <tbody>
                {att.map((a) => (
                  <tr key={a.id} className="border-t border-navy-100">
                    <td className="p-2.5 text-navy-700">{a.date}</td><td className="p-2.5 text-navy-700">{a.guard_name}</td>
                    <td className="p-2.5 text-center"><StatusPill status={a.status} /></td>
                    <td className="p-2.5 text-center text-navy-500 text-xs">{a.method}</td>
                    <td className="p-2.5 text-center text-navy-600">{a.check_in}</td><td className="p-2.5 text-center text-navy-600">{a.check_out}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
